import { LIVENESS_CONFIG } from "./livenessConfig";

export const LIVENESS_PHASES = Object.freeze({
  POSITION: "POSITION",
  TURN_FIRST_SIDE: "TURN_FIRST_SIDE",
  TURN_RETURN_CENTER: "TURN_RETURN_CENTER",
  TURN_OPPOSITE_SIDE: "TURN_OPPOSITE_SIDE",
  NOD_CENTER: "NOD_CENTER",
  NOD_MOVE: "NOD_MOVE",
  NOD_RETURN_CENTER: "NOD_RETURN_CENTER",
  NOD_OPPOSITE: "NOD_OPPOSITE",
  NOD_FINAL_CENTER: "NOD_FINAL_CENTER",
  COMPLETE: "COMPLETE",
  TIMED_OUT: "TIMED_OUT",
});

const PHASE_PROMPTS = Object.freeze({
  [LIVENESS_PHASES.POSITION]: "positioning",
  [LIVENESS_PHASES.TURN_FIRST_SIDE]: "turnSideToSide",
  [LIVENESS_PHASES.TURN_RETURN_CENTER]: "returnCenter",
  [LIVENESS_PHASES.TURN_OPPOSITE_SIDE]: "turnOtherSide",
  [LIVENESS_PHASES.NOD_CENTER]: "returnCenter",
  [LIVENESS_PHASES.NOD_MOVE]: "lookDown",
  [LIVENESS_PHASES.NOD_RETURN_CENTER]: "returnCenterAfterNod",
  [LIVENESS_PHASES.NOD_OPPOSITE]: "lookUp",
  [LIVENESS_PHASES.NOD_FINAL_CENTER]: "returnCenterAfterRaise",
  [LIVENESS_PHASES.COMPLETE]: "complete",
  [LIVENESS_PHASES.TIMED_OUT]: "timeout",
});

const getStep = (phase) => {
  if (phase === LIVENESS_PHASES.POSITION) return 0;
  if (
    phase === LIVENESS_PHASES.TURN_FIRST_SIDE ||
    phase === LIVENESS_PHASES.TURN_RETURN_CENTER ||
    phase === LIVENESS_PHASES.TURN_OPPOSITE_SIDE
  ) {
    return 1;
  }
  if (
    phase === LIVENESS_PHASES.NOD_CENTER ||
    phase === LIVENESS_PHASES.NOD_MOVE ||
    phase === LIVENESS_PHASES.NOD_RETURN_CENTER ||
    phase === LIVENESS_PHASES.NOD_OPPOSITE ||
    phase === LIVENESS_PHASES.NOD_FINAL_CENTER
  ) {
    return 2;
  }
  return 3;
};

/**
 * 活体动作连续帧状态机。
 *
 * 单独一帧不能完成动作。人脸丢失或移出有效区域时，只会清空当前动作
 * 的连续确认帧；之前已经完成的方向仍然保留，用于 UI 绿色弧段反馈。
 */
export class LivenessChallengeMachine {
  constructor(options = {}) {
    this.options = { ...LIVENESS_CONFIG, ...options };
    this.reset();
  }

  reset() {
    this.phase = LIVENESS_PHASES.POSITION;
    this.confirmedFrames = 0;
    this.candidate = "";
    this.firstTurnDirection = "";
    this.nodDirection = "";
    this.completedTurns = { positive: false, negative: false };
    this.completedNods = { positive: false, negative: false };
    this.nodMotionComplete = false;
    this.neutralSamples = [];
    this.baseline = null;
    this.smoothedPose = null;
    this.startedAt = null;
    return this.getSnapshot();
  }

  resetConfirmation() {
    this.confirmedFrames = 0;
    this.candidate = "";
  }

  confirm(candidate) {
    if (!candidate) {
      this.resetConfirmation();
      return false;
    }

    if (candidate !== this.candidate) {
      this.candidate = candidate;
      this.confirmedFrames = 1;
    } else {
      this.confirmedFrames += 1;
    }

    if (this.confirmedFrames < this.options.confirmationFrames) return false;
    this.resetConfirmation();
    return true;
  }

  setPhase(phase) {
    this.phase = phase;
    this.resetConfirmation();
  }

  smoothPose(yaw, pitch) {
    if (!this.smoothedPose) {
      this.smoothedPose = { yaw, pitch };
      return this.smoothedPose;
    }

    const weight = this.options.poseSmoothing;
    this.smoothedPose = {
      yaw: this.smoothedPose.yaw * (1 - weight) + yaw * weight,
      pitch: this.smoothedPose.pitch * (1 - weight) + pitch * weight,
    };
    return this.smoothedPose;
  }

  getSnapshot(overrides = {}) {
    return {
      phase: this.phase,
      prompt: PHASE_PROMPTS[this.phase],
      step: getStep(this.phase),
      faceDetected: false,
      positionValid: false,
      complete: this.phase === LIVENESS_PHASES.COMPLETE,
      timedOut: this.phase === LIVENESS_PHASES.TIMED_OUT,
      // 这些值用于驱动分段椭圆。已经完成的方向会保持绿色，实时
      // yaw / pitch 则负责显示当前头部移动方向。
      completedTurns: { ...this.completedTurns },
      completedNods: { ...this.completedNods },
      nodMotionComplete: this.nodMotionComplete,
      firstTurnDirection: this.firstTurnDirection,
      confirmedFrames: this.confirmedFrames,
      requiredFrames: this.options.confirmationFrames,
      ...overrides,
    };
  }

  update({ faceDetected, positionValid, yaw = 0, pitch = 0, timestamp }) {
    const now = Number.isFinite(timestamp) ? timestamp : Date.now();
    if (this.startedAt === null) this.startedAt = now;

    if (
      this.phase !== LIVENESS_PHASES.COMPLETE &&
      now - this.startedAt >= this.options.timeoutMs
    ) {
      this.setPhase(LIVENESS_PHASES.TIMED_OUT);
      return this.getSnapshot({ faceDetected, positionValid });
    }

    if (
      this.phase === LIVENESS_PHASES.COMPLETE ||
      this.phase === LIVENESS_PHASES.TIMED_OUT
    ) {
      return this.getSnapshot({ faceDetected, positionValid });
    }

    if (!faceDetected) {
      this.resetConfirmation();
      return this.getSnapshot({ prompt: "faceMissing" });
    }

    if (!positionValid) {
      this.resetConfirmation();
      return this.getSnapshot({
        faceDetected: true,
        prompt: "positioning",
      });
    }

    const pose = this.smoothPose(yaw, pitch);
    const relativeYaw = this.baseline ? pose.yaw - this.baseline.yaw : pose.yaw;
    const relativePitch = this.baseline
      ? pose.pitch - this.baseline.pitch
      : pose.pitch;
    const yawCentered = Math.abs(relativeYaw) <= this.options.neutralYawDegrees;
    const pitchCentered =
      Math.abs(relativePitch) <= this.options.neutralPitchDegrees;

    switch (this.phase) {
      case LIVENESS_PHASES.POSITION: {
        if (!yawCentered || !pitchCentered) {
          this.neutralSamples = [];
          this.resetConfirmation();
          break;
        }

        this.neutralSamples.push(pose);
        if (this.confirm("center")) {
          const totals = this.neutralSamples.reduce(
            (sum, sample) => ({
              yaw: sum.yaw + sample.yaw,
              pitch: sum.pitch + sample.pitch,
            }),
            { yaw: 0, pitch: 0 },
          );
          this.baseline = {
            yaw: totals.yaw / this.neutralSamples.length,
            pitch: totals.pitch / this.neutralSamples.length,
          };
          this.setPhase(LIVENESS_PHASES.TURN_FIRST_SIDE);
        }
        break;
      }

      case LIVENESS_PHASES.TURN_FIRST_SIDE: {
        const direction =
          relativeYaw >= this.options.turnThresholdDegrees
            ? "positive"
            : relativeYaw <= -this.options.turnThresholdDegrees
              ? "negative"
              : "";
        if (this.confirm(direction)) {
          this.firstTurnDirection = direction;
          this.completedTurns[direction] = true;
          this.setPhase(LIVENESS_PHASES.TURN_RETURN_CENTER);
        }
        break;
      }

      case LIVENESS_PHASES.TURN_RETURN_CENTER:
        if (this.confirm(yawCentered && pitchCentered ? "center" : "")) {
          this.setPhase(LIVENESS_PHASES.TURN_OPPOSITE_SIDE);
        }
        break;

      case LIVENESS_PHASES.TURN_OPPOSITE_SIDE: {
        const reachedOppositeSide =
          this.firstTurnDirection === "positive"
            ? relativeYaw <= -this.options.turnThresholdDegrees
            : relativeYaw >= this.options.turnThresholdDegrees;
        if (this.confirm(reachedOppositeSide ? "opposite" : "")) {
          const oppositeDirection =
            this.firstTurnDirection === "positive" ? "negative" : "positive";
          this.completedTurns[oppositeDirection] = true;
          this.setPhase(LIVENESS_PHASES.NOD_CENTER);
        }
        break;
      }

      case LIVENESS_PHASES.NOD_CENTER:
        if (this.confirm(yawCentered && pitchCentered ? "center" : "")) {
          this.setPhase(LIVENESS_PHASES.NOD_MOVE);
        }
        break;

      case LIVENESS_PHASES.NOD_MOVE: {
        // MediaPipe 变换矩阵中，低头为负 pitch，抬头为正 pitch。
        // 流程先确认低头，用户回正以后再单独确认抬头。
        const lookingDown =
          relativePitch <= -this.options.nodThresholdDegrees;
        if (this.confirm(yawCentered && lookingDown ? "negative" : "")) {
          this.nodDirection = "negative";
          this.completedNods.negative = true;
          this.nodMotionComplete = true;
          this.setPhase(LIVENESS_PHASES.NOD_RETURN_CENTER);
        }
        break;
      }

      case LIVENESS_PHASES.NOD_RETURN_CENTER:
        if (this.confirm(yawCentered && pitchCentered ? "center" : "")) {
          this.setPhase(LIVENESS_PHASES.NOD_OPPOSITE);
        }
        break;

      case LIVENESS_PHASES.NOD_OPPOSITE: {
        const lookingUp = relativePitch >= this.options.nodThresholdDegrees;
        if (this.confirm(yawCentered && lookingUp ? "positive" : "")) {
          this.completedNods.positive = true;
          this.setPhase(LIVENESS_PHASES.NOD_FINAL_CENTER);
        }
        break;
      }

      case LIVENESS_PHASES.NOD_FINAL_CENTER:
        if (this.confirm(yawCentered && pitchCentered ? "center" : "")) {
          this.setPhase(LIVENESS_PHASES.COMPLETE);
        }
        break;

      default:
        break;
    }

    return this.getSnapshot({
      faceDetected: true,
      positionValid: true,
      yaw: relativeYaw,
      pitch: relativePitch,
    });
  }
}
